import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';

import { isNullOrUndefined } from '@utils/utils';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { ApiContext } from '@api/api.tsx';
import {
  ModeEnum,
  ReservationRampDashboardDataOutDTO,
  ReservationRampDashboardOutDTO,
  TvFieldSettingDTO,
} from '@api/ringil-api.v2.tsx';
import {
  formatTimeIntervalFromStringInTzWithLocale,
  formatTimeFromDayjsInTz,
  formatDateFromDayjsWithLocale,
  dayjsFromApiUtcInTz,
  isSameDayInTz,
} from '@utils/dateWithTz';

interface ComponentProps {}

const DEFAULT_TIMEZONE = 'Europe/Prague';

/** Blocks a card can draw; each holds the fields that share a line. */
const MIN_LINES = 3;
const MAX_LINES = 6;
/** A character is roughly half its font size wide in this typeface; only used to choose a font. */
const CHAR_WIDTH_RATIO = 0.5;
/** Height one rendered line takes, as a multiple of the font size: line-height plus a little breathing room. */
const LINE_BOX = 1.45;
/** The card's own vertical padding and borders, in px. */
const CARD_CHROME = 10;
/** Size for this share of the cards; the heaviest few wrap past their box and are cut, as they would be anyway. */
const FIT_PERCENTILE = 0.8;

/** Width available for text inside one card: page padding, the ramp-name column, the gaps, the card padding. */
const cardTextWidth = (columns: number) => {
  const contentWidth = window.innerWidth * 0.95;
  const cardsArea = (contentWidth - 3 * 32) * 0.75 + 2 * 32;
  return Math.max(60, (cardsArea - (columns - 1) * 16) / columns - 24);
};

/**
 * Lines a block of one or two fields renders on. A 'Shorten' field cannot wrap, so a block whose
 * shown fields are all shortened is always a single line; otherwise the whole ones wrap and the
 * shortened sibling claims at most the rest of a line.
 */
const blockLines = (parts: { text: string; field?: TvFieldSettingDTO }[], charsPerLine: number) => {
  const present = parts.filter(p => p.field && p.field.mode !== ModeEnum.HIDDEN && p.text);
  if (present.length === 0) return 0;
  if (present.every(p => p.field!.mode === ModeEnum.TRUNCATED)) return 1;
  const chars = present.reduce(
    (sum, p) => sum + (p.field!.mode === ModeEnum.TRUNCATED ? Math.min(p.text.length, charsPerLine) : p.text.length),
    0,
  );
  return Math.max(1, Math.ceil(chars / charsPerLine));
};

/** Lines the fullest card needs at a given font, from character counts - no DOM measurement. */
const neededLines = (rows: ReservationRampDashboardOutDTO[], cap: number, textPx: number, rowPx: number) => {
  for (let budget = MIN_LINES; budget < MAX_LINES; budget++) {
    const font = Math.max(11, Math.min((rowPx - CARD_CHROME) / (budget * LINE_BOX), 30));
    const charsPerLine = Math.max(8, textPx / (CHAR_WIDTH_RATIO * font));
    const perCard = rows
      .flatMap(ramp =>
        ramp.reservations.slice(0, cap).map(reservation => {
          const tv = ramp.tvDashboard;
          return (
            blockLines(
              [
                { text: reservation.reservationName ?? '', field: tv.reservationName },
                { text: (reservation.orderIds ?? []).join(', '), field: tv.orderIds },
              ],
              charsPerLine,
            ) +
            (tv.time && tv.time.mode !== ModeEnum.HIDDEN ? 1 : 0) + // the date always renders on one line
            blockLines(
              [
                { text: reservation.carrierName ?? '', field: tv.carrier },
                { text: reservation.carPlate ?? '', field: tv.carPlate },
              ],
              charsPerLine,
            )
          );
        }),
      )
      .sort((a, b) => a - b);
    if (perCard.length === 0) return MIN_LINES;
    const atPercentile = perCard[Math.min(perCard.length - 1, Math.ceil(perCard.length * FIT_PERCENTILE) - 1)];
    // Size for what the cards really need, not for the budget that proved sufficient: sizing for
    // unused lines leaves the row half empty.
    const needed = Math.max(MIN_LINES, atPercentile);
    if (needed <= budget) return needed;
  }
  return MAX_LINES;
};

const LocationPanelPage: React.FC<ComponentProps> = () => {
  const { tr } = useTranslationLgs();
  const { getReservationForRampDashboard } = useContext(ApiContext);
  const [data, setData] = useState<ReservationRampDashboardOutDTO[]>();
  const { locationId } = useParams();

  useEffect(() => {
    load();
    const timer = setInterval(() => load(), 15000);
    return () => clearInterval(timer);
  }, [locationId]);

  const startOf = (r: ReservationRampDashboardDataOutDTO) => r.timeslot.utcStart ?? r.timeslot.since;
  const timezoneOf = (r: ReservationRampDashboardDataOutDTO) => r.timeslot.timezoneId ?? DEFAULT_TIMEZONE;

  const load = () => {
    getReservationForRampDashboard(locationId, d => setData(d.filter(ramp => ramp.reservations.length > 0)));
  };

  if (isNullOrUndefined(data)) return <></>;

  const settings = data[0]?.tvDashboard;
  const cap = Math.max(settings?.columnsCount ?? 3, 1);
  // Tracks follow the configured count, not how many cards happen to exist: card width and therefore
  // the font stay put as slots pass during the day.
  const columns = cap;
  // Font follows the height a row actually has, not the number of ramps.
  const rowPx = (window.innerHeight - 66) / Math.max(data.length, 1) - 32;
  const cardTextPx = cardTextWidth(columns);
  // Budget the lines the cards actually need, so text wraps before it is cut. A field set to
  // 'Shorten' is one line whatever its length, which is what keeps the estimate honest.
  const lineBudget = neededLines(data, cap, cardTextPx, rowPx);
  const cardFont = Math.max(11, Math.min((rowPx - CARD_CHROME) / (lineBudget * LINE_BOX), 30));
  const nameFont = Math.max(14, Math.min(rowPx / 3.2, 48));

  const shown = (field?: TvFieldSettingDTO) => !!field && field.mode !== ModeEnum.HIDDEN;
  const truncated = (field?: TvFieldSettingDTO) => field?.mode === ModeEnum.TRUNCATED;

  const orderIds = (reservation: ReservationRampDashboardDataOutDTO, field?: TvFieldSettingDTO) => {
    const ids = reservation.orderIds ?? [];
    return truncated(field) && ids.length > 1 ? `${ids[0]}, …` : ids.join(', ');
  };

  // Inside a block a 'Shorten' field gives way to its 'Show whole' sibling: it shrinks and ends in an
  // ellipsis, the whole one keeps its width. With both shortened they shrink together.
  const blockClass = (...fields: TvFieldSettingDTO[]) =>
    fields.filter(shown).some(truncated) ? 'flex flex-row min-w-0' : undefined;
  const shortened = (field: TvFieldSettingDTO, sibling: TvFieldSettingDTO, bold?: boolean) =>
    clsx(
      truncated(field) ? 'min-w-0 truncate' : shown(sibling) && truncated(sibling) && 'shrink-0',
      bold && 'font-bold',
    );

  return (
    <div className="h-screen overflow-hidden">
      <div className="w-full px-[2.5vw] bg-stone-100 grid grid-cols-4 gap-x-20 items-center h-[6.11111111vh]">
        <div className="">{/* logo */}</div>
        <div className="flex grow flex-row items-center shrink-0 col-span-3">
          <span className="text-zinc-800 inline-block" style={{ fontSize: `${Math.round(Math.max(14, nameFont * 0.4))}px` }}>
            {tr(`LocationPanelPage.timeslots`, `Timeslots`)}
          </span>
          <img src="/images/powered-ringil.png" alt="Powered by Ringil" className="w-[8.59375vw] ml-auto inline-block" />
        </div>
      </div>

      <div className="flex flex-col h-[calc(100vh-4.125rem)]">
        {data.map((ramp, rampIndex) => (
          <div
            className="bg-white border-b-content-tertiary border-solid border-0 last:border-none border-b overflow-hidden even:bg-[var(--content-brandmedium)] grid grid-cols-4 px-[2.5vw] gap-x-[2rem] py-4 items-center"
            key={ramp.rampId}
            style={{ height: data.length > 3 ? `${100 / data.length}%` : '33.333%' }}
          >
            <div
              className="text-zinc-800 col-span-1 font-semibold overflow-hidden leading-tight line-clamp-2"
              style={{ fontSize: `${Math.round(nameFont)}px` }}
            >
              {ramp.name}
            </div>

            <div className="col-span-3 flex flex-col gap-y-2 h-full min-h-0 justify-center">
              <div
                className="grid gap-x-[1rem] flex-1 min-h-0 overflow-hidden content-start"
                style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
              >
                {ramp.reservations.slice(0, cap).map((reservation, index) => (
                  <div
                    key={index}
                    className={clsx(
                      'text-[var(--content-primary)] rounded-lg px-3 py-1 overflow-hidden border border-solid border-[var(--content-tertiary)]',
                      rampIndex % 2 === 0 ? 'bg-[var(--content-brandmedium)]' : 'bg-white',
                    )}
                    style={{ fontSize: `${Math.round(cardFont)}px` }}
                  >
                    {/* Fields keep their fixed order; what no longer fits is cut off the bottom of the card. */}
                    {(shown(ramp.tvDashboard.reservationName) || shown(ramp.tvDashboard.orderIds)) && (
                      <div className={blockClass(ramp.tvDashboard.reservationName, ramp.tvDashboard.orderIds)}>
                        {shown(ramp.tvDashboard.reservationName) && (
                          <span
                            className={shortened(
                              ramp.tvDashboard.reservationName,
                              ramp.tvDashboard.orderIds,
                              ramp.tvDashboard.reservationName.bold,
                            )}
                          >
                            {reservation.reservationName}
                          </span>
                        )}
                        {shown(ramp.tvDashboard.orderIds) && (reservation.orderIds ?? []).length > 0 && (
                          <span
                            className={shortened(ramp.tvDashboard.orderIds, ramp.tvDashboard.reservationName, ramp.tvDashboard.orderIds.bold)}
                          >
                            {shown(ramp.tvDashboard.reservationName)
                              ? ` (${orderIds(reservation, ramp.tvDashboard.orderIds)})`
                              : orderIds(reservation, ramp.tvDashboard.orderIds)}
                          </span>
                        )}
                      </div>
                    )}

                    {shown(ramp.tvDashboard.time) && (
                      <div className={clsx('truncate', ramp.tvDashboard.time.bold && 'font-bold')}>
                        {truncated(ramp.tvDashboard.time)
                          ? formatTimeFromDayjsInTz(dayjsFromApiUtcInTz(startOf(reservation), timezoneOf(reservation)), timezoneOf(reservation))
                          : formatTimeIntervalFromStringInTzWithLocale(
                              false,
                              startOf(reservation),
                              reservation.timeslot.utcEnd ?? reservation.timeslot.till,
                              timezoneOf(reservation),
                              tr,
                            )}
                        {/* Everything on screen is today, so the date only earns its place when it isn't. */}
                        {!isSameDayInTz(startOf(reservation), timezoneOf(reservation)) && (
                          <span className="text-zinc-400">
                            {' '}
                            {formatDateFromDayjsWithLocale(dayjsFromApiUtcInTz(startOf(reservation), timezoneOf(reservation)))}
                          </span>
                        )}
                      </div>
                    )}

                    {(shown(ramp.tvDashboard.carrier) || shown(ramp.tvDashboard.carPlate)) && (
                      <div className={blockClass(ramp.tvDashboard.carrier, ramp.tvDashboard.carPlate)}>
                        {shown(ramp.tvDashboard.carrier) && (
                          <span className={shortened(ramp.tvDashboard.carrier, ramp.tvDashboard.carPlate, ramp.tvDashboard.carrier.bold)}>
                            {reservation.carrierName}
                          </span>
                        )}
                        {shown(ramp.tvDashboard.carPlate) && reservation.carPlate && (
                          <span className={shortened(ramp.tvDashboard.carPlate, ramp.tvDashboard.carrier, ramp.tvDashboard.carPlate.bold)}>
                            {shown(ramp.tvDashboard.carrier) ? ` (${reservation.carPlate})` : reservation.carPlate}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default withAuthenticationRequired(LocationPanelPage);
